<script setup lang="ts">
/* ----------------------------------------------------------------------------------------------
 * Global floating Luma assistant.
 *
 * A glass seahorse button pinned to the bottom-right of every page. Clicking it slides a chat
 * panel in from the right. The panel drives the shared chat-session store directly (create +
 * send) without touching the router, so Luma is reachable anywhere without navigating to /chats.
 *
 * Open state lives in `luma-dock-store` rather than here, so other screens can open the drawer
 * in place — and hand it a question to ask (ENG-436: the search page routes questions here
 * instead of to the full-screen chat view).
 * -------------------------------------------------------------------------------------------- */

import type { ChatSurface } from '@/composables/analytics/analytics-service';
import type { ApalumaChatThread } from '@/types/api-response/chat-session-types.ts';
import { Icon } from '@iconify/vue';
import { onKeyStroke } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import seahorse from '@/assets/images/logo/logo-luma-seahorse.png';
import ChatInput from '@/components/chat/chat-box/chat-input.vue';
import ChatThreadContextMenu from '@/components/chat/chat-thread-context-menu.vue';
import ChatMessage from '@/components/home-page/chat/chat-history/chat-message/chat-message.vue';
import SearchChatHistory from '@/components/home-page/chat/sidebar/search-chat-history.vue';
import { useChatSessionStore } from '@/stores/chat-session-store.ts';
import { useLumaDockStore } from '@/stores/luma-dock-store.ts';

/* ---------------------------------------------------------------------------------------------- */

const chatSessionStore = useChatSessionStore();
const { sessionMessages, selectedSession, sessionState, hasMoreSessions, floatingAssistantActive } = storeToRefs(chatSessionStore);
const {
   createSession,
   sendMessage,
   clearSelectedSession,
   updateSessionTitleOnFirstMessage,
   fetchSessions,
   searchSessions,
   selectSession,
   deleteSession,
   renameSession,
   loadMoreSessions,
} = chatSessionStore;

const lumaDock = useLumaDockStore();
const { isOpen } = storeToRefs(lumaDock);

// When true, the panel widens to reveal the past-chats rail alongside the conversation.
const showRail = ref(false);
const chatInput = ref('');
const isSending = ref(false);
const errorMessage = ref<string | null>(null);

const messagesContainer = useTemplateRef<HTMLElement>('messagesContainer');

// The routed chat the user was viewing before opening the assistant. The assistant
// reuses the shared session store, so we snapshot on open and restore on close so
// the routed chat isn't discarded. TODO: separate floating-assistant session state
// so this snapshot/restore (and store.floatingAssistantActive) can go away.
let savedSession: typeof selectedSession.value = null;
let savedMessages: typeof sessionMessages.value = [];

/* ---------------------------------------------------------------------------------------------- */

// The "Ask Luma" bubble is visible on every screen whenever the panel is closed.
const showTrigger = computed(() => !isOpen.value);

/* ----------------------------------------------------------------------------------------------
 * Past chats: group the loaded sessions into human-friendly buckets by recency so the rail
 * reads like a familiar chat history (Today / Yesterday / Previous 7 days / Older).
 * -------------------------------------------------------------------------------------------- */
function startOfDay(d: Date): number {
   return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const groupedSessions = computed(() => {
   const today = startOfDay(new Date());
   const dayMs = 86_400_000;
   const buckets: { label: string; sessions: ApalumaChatThread[] }[] = [
      { label: 'Today', sessions: [] },
      { label: 'Yesterday', sessions: [] },
      { label: 'Previous 7 days', sessions: [] },
      { label: 'Older', sessions: [] },
   ];

   for (const session of sessionState.value.sessions) {
      const stamp = startOfDay(new Date(session.updated_at || session.created_at));
      if (stamp >= today)
         buckets[0].sessions.push(session);
      else if (stamp >= today - dayMs)
         buckets[1].sessions.push(session);
      else if (stamp >= today - dayMs * 7)
         buckets[2].sessions.push(session);
      else
         buckets[3].sessions.push(session);
   }

   return buckets.filter(b => b.sessions.length > 0);
});

// A few starter prompts shown on the empty state. Kept organization-agnostic so
// they read sensibly for any customer rather than referencing a specific agency.
const examplePrompts = [
   'Summarize the environmental history of a facility',
   'What conditions are typically included in discharge permits for water reuse?',
   'Show a timeline of enforcement actions for a facility',
];

/* ---------------------------------------------------------------------------------------------- */

function onOpened() {
   // Preserve the routed chat: snapshot the active session/messages and flag the
   // assistant open so the routed view doesn't treat our clear as a deletion and
   // redirect. Restored in onClosed().
   savedSession = selectedSession.value;
   savedMessages = sessionMessages.value;
   floatingAssistantActive.value = true;
   // Always open onto a fresh chat (the "How can I help?" state) rather than
   // resuming the last session — past chats stay reachable via the history rail.
   startNewChat();
   scrollToBottom();
}

function onClosed() {
   // Restore the routed chat the user was viewing before the assistant opened.
   selectedSession.value = savedSession;
   sessionMessages.value = savedMessages;
   floatingAssistantActive.value = false;
   savedSession = null;
   savedMessages = [];
}

function toggleRail() {
   showRail.value = !showRail.value;
   // Lazily load the history the first time the rail is revealed.
   if (showRail.value && sessionState.value.sessions.length === 0)
      fetchSessions(true);
}

function startNewChat() {
   clearSelectedSession();
   errorMessage.value = null;
   chatInput.value = '';
}

async function openSession(sessionId: number) {
   errorMessage.value = null;
   chatInput.value = '';
   await selectSession(sessionId);
   await scrollToBottom();
}

function handleSearchUpdate(value: string) {
   sessionState.value.titleFilter = value;
}

function handleSearch(query: string) {
   searchSessions(query);
}

async function scrollToBottom() {
   await nextTick();
   const el = messagesContainer.value;
   if (el)
      el.scrollTop = el.scrollHeight;
}

/**
 * Send `message`, reporting `surface` as where it was asked.
 *
 * The surface is a separate entry point rather than an optional second argument
 * on `handleSendMessage`, because the input already emits its own second
 * argument. Sharing that position would silently file every typed question
 * under whatever the input happened to pass.
 */
async function askInDrawer(message: string, surface: ChatSurface) {
   const trimmed = message.trim();
   if (!trimmed || isSending.value)
      return;

   errorMessage.value = null;
   isSending.value = true;

   try {
      // Reuse the active session, or spin up a new one on the first message.
      let session = selectedSession.value;
      if (!session) {
         session = await createSession();
         if (!session) {
            errorMessage.value = 'Failed to start a chat. Please try again.';
            return;
         }
      }

      chatInput.value = '';
      const response = await sendMessage(trimmed, session.id, { surface });

      if (response?.thread) {
         updateSessionTitleOnFirstMessage(response.thread);
      }

      await scrollToBottom();
   }
   catch (error) {
      console.error('Error sending message to Luma:', error);
      errorMessage.value = 'Something went wrong. Please try again.';
   }
   finally {
      isSending.value = false;
   }
}

/** A question typed into the drawer, or picked from its suggested prompts. */
function handleSendMessage(message: string) {
   return askInDrawer(message, 'chat_drawer');
}

/* ---------------------------------------------------------------------------------------------- */

// Keep the newest message in view as content streams in.
watch(
   () => sessionMessages.value.map(m => `${m.id}:${m.content}:${m.status}`).join('|'),
   () => {
      if (isOpen.value)
         scrollToBottom();
   },
);

/**
 * React to the dock: open/close the panel, and ask anything handed over with it.
 *
 * One watcher over both bits of dock state rather than two, because the order of
 * the two effects matters and must not come down to which watcher was created
 * first — the snapshot in `onOpened` has to be taken before a handed-over
 * question starts a thread of its own.
 */
watch(
   [isOpen, () => lumaDock.pendingQuestion],
   ([nowOpen], [wasOpen]) => {
      if (nowOpen && !wasOpen)
         onOpened();
      else if (!nowOpen && wasOpen)
         onClosed();

      if (!nowOpen)
         return;

      // A question another screen handed over, such as the search bar. Asked in
      // a fresh chat so it can't land mid-conversation when the drawer was
      // already open on something else. Its surface comes over with it, so the
      // ask is credited to the input the user actually used.
      const pending = lumaDock.takePendingAsk();
      if (pending) {
         startNewChat();
         askInDrawer(pending.question, pending.surface);
      }
   },
);

onKeyStroke('Escape', () => {
   if (isOpen.value)
      lumaDock.close();
});
</script>

<template>
   <!-- Floating trigger — a teal droplet with the seahorse and a small "Ask Luma" label inside. -->
   <Transition name="luma-trigger">
      <button
         v-if="showTrigger"
         data-tour="luma"
         type="button"
         class="luma-bubble fixed bottom-6 right-6 z-[80] size-[92px] rounded-full
         transition-transform duration-200 hover:scale-105 active:scale-95"
         aria-label="Ask Luma"
         @click="lumaDock.open()"
      >
         <img
            :src="seahorse"
            alt=""
            class="luma-swim absolute left-[43%] top-[24%] z-10 h-11 w-auto -translate-x-1/2 -translate-y-1/2
            object-contain drop-shadow-sm"
         />
         <!-- "ASK LUMA" curved along the bottom inner arc -->
         <svg class="absolute inset-0 z-10 size-full pointer-events-none" viewBox="0 0 100 100" aria-hidden="true">
            <path id="luma-arc" d="M 12,50 a 38,38 0 1,0 76,0 a 38,38 0 1,0 -76,0" fill="none" />
            <text fill="#0B3436" font-size="10" font-weight="700" letter-spacing="1.2" text-anchor="middle">
               <textPath href="#luma-arc" startOffset="25%">ASK LUMA</textPath>
            </text>
         </svg>
      </button>
   </Transition>

   <!-- Slide-in chat panel -->
   <Transition name="luma-panel">
      <!-- A plain div, not an <aside>: `role="dialog"` isn't an allowed role for
           <aside>, which axe flags — and this is a dialog, not a complementary
           landmark. -->
      <div
         v-if="isOpen"
         class="luma-glass-panel fixed inset-y-0 right-0 z-[80] flex w-fit max-w-[100vw] flex-row
         border-l border-white/40 shadow-[0_8px_40px_rgba(2,77,80,0.18)]"
         role="dialog"
         aria-label="Luma assistant"
      >
         <!-- ═══ PAST-CHATS RAIL ═══ -->
         <Transition name="luma-rail">
            <div
               v-if="showRail"
               class="flex w-[260px] shrink-0 flex-col border-r border-black/5 bg-white/30"
            >
               <div class="flex items-center gap-2 px-4 py-4 border-b border-black/5">
                  <p class="flex-1 text-sm font-semibold text-[#0B3436]">
                     Chats
                  </p>
                  <button
                     type="button"
                     class="grid place-items-center size-8 rounded-full text-black/50 hover:bg-black/5
                     hover:text-black transition-colors"
                     aria-label="New chat"
                     title="New chat"
                     @click="startNewChat"
                  >
                     <Icon :icon="IconLib.actions.plus" class="size-4" />
                  </button>
               </div>

               <div class="px-3 pt-3">
                  <SearchChatHistory
                     :model-value="sessionState.titleFilter"
                     @update:model-value="handleSearchUpdate"
                     @search="handleSearch"
                  />
               </div>

               <div class="flex-1 overflow-y-auto px-3 pb-4 pt-3">
                  <!-- Loading skeletons -->
                  <div v-if="sessionState.isLoading && sessionState.sessions.length === 0" class="flex flex-col gap-2">
                     <div v-for="i in 6" :key="`sk-${i}`" class="h-7 rounded-lg bg-black/5 animate-pulse" />
                  </div>

                  <!-- Empty state -->
                  <div
                     v-else-if="sessionState.sessions.length === 0"
                     class="flex flex-col items-center justify-center py-10 text-center"
                  >
                     <Icon :icon="IconLib.chat.chats" class="size-8 text-black/20 mb-2" />
                     <p class="text-sm text-black/50">
                        {{ sessionState.titleFilter ? 'No chats found' : 'No chats yet' }}
                     </p>
                  </div>

                  <!-- Grouped history -->
                  <template v-else>
                     <div v-for="group in groupedSessions" :key="group.label" class="mb-3">
                        <p class="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-black/40">
                           {{ group.label }}
                        </p>
                        <div
                           v-for="session in group.sessions"
                           :key="session.id"
                           class="group relative flex items-center rounded-lg text-sm text-black/80
                           hover:bg-white transition-colors duration-150"
                           :class="selectedSession?.id === session.id ? 'bg-white' : ''"
                        >
                           <button
                              type="button"
                              class="w-full min-w-0 text-left truncate py-1.5 ps-2 pe-3 group-hover:pe-8"
                              @click="openSession(session.id)"
                           >
                              {{ session.title || 'Untitled chat' }}
                           </button>
                           <ChatThreadContextMenu
                              :session="session"
                              :delete-session="(id) => deleteSession(id)"
                              :rename-session="(id, title) => renameSession(id, title)"
                              :trigger-aria-label="`More options for ${session.title || 'Untitled chat'}`"
                              class="absolute right-1.5 top-1/2 -translate-y-1/2 rounded py-0.5 px-1
                              hover:bg-black/10 transition-all duration-150 opacity-0 group-hover:opacity-100
                              pointer-events-none group-hover:pointer-events-auto"
                           />
                        </div>
                     </div>

                     <button
                        v-if="hasMoreSessions"
                        type="button"
                        class="w-full py-1.5 text-sm text-teal-700 font-medium rounded-lg
                        hover:bg-teal-600/5 transition-colors"
                        @click="loadMoreSessions"
                     >
                        Load more
                     </button>
                  </template>
               </div>
            </div>
         </Transition>

         <!-- ═══ CONVERSATION COLUMN ═══ -->
         <!-- Fixed width so it never reflows while the rail animates open/closed. -->
         <div class="flex w-[440px] max-w-[100vw] shrink-0 flex-col">
            <!-- Header -->
            <header class="flex items-center gap-3 px-5 py-4 border-b border-black/5">
               <button
                  type="button"
                  class="grid place-items-center size-9 rounded-full text-black/50 hover:bg-black/5
                  hover:text-black transition-colors shrink-0"
                  :class="showRail ? 'bg-black/5 text-black' : ''"
                  :aria-label="showRail ? 'Hide past chats' : 'Show past chats'"
                  :aria-pressed="showRail"
                  title="Past chats"
                  @click="toggleRail"
               >
                  <Icon :icon="IconLib.chat.chats" class="size-5" />
               </button>
               <img :src="seahorse" alt="" class="h-9 w-auto object-contain shrink-0" />
               <div class="flex-1 min-w-0">
                  <p class="text-base font-semibold text-[#0B3436] leading-tight">
                     Luma
                  </p>
                  <p class="text-xs text-black/50 leading-tight">
                     Your environmental records assistant
                  </p>
               </div>

               <button
                  type="button"
                  class="grid place-items-center size-9 rounded-full text-black/50 hover:bg-black/5
                  hover:text-black transition-colors"
                  aria-label="Start a new chat"
                  title="New chat"
                  @click="startNewChat"
               >
                  <svg class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14" /></svg>
               </button>
               <button
                  type="button"
                  class="grid place-items-center size-9 rounded-full text-black/50 hover:bg-black/5
               hover:text-black transition-colors"
                  aria-label="Close Luma"
                  @click="lumaDock.close()"
               >
                  <svg class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
               </button>
            </header>

            <!-- Messages -->
            <div ref="messagesContainer" class="flex-1 overflow-y-auto px-5 py-6">
               <!-- Empty state -->
               <div v-if="sessionMessages.length === 0" class="h-full flex flex-col items-center justify-center text-center">
                  <img :src="seahorse" alt="" class="h-14 w-auto object-contain mb-5" />
                  <h2 class="text-lg font-semibold text-[#0B3436]">
                     How can I help?
                  </h2>
                  <p class="text-sm text-black/50 mt-1 max-w-[18rem]">
                     Ask about permits, facilities, enforcement history, and more.
                  </p>

                  <div class="w-full flex flex-col gap-2 mt-6">
                     <button
                        v-for="prompt in examplePrompts"
                        :key="prompt"
                        type="button"
                        class="text-left text-sm text-black/70 rounded-xl border border-teal-600/15 bg-white/60
                     px-4 py-3 transition-all hover:border-teal-600/40 hover:bg-white hover:text-teal-700"
                        @click="handleSendMessage(prompt)"
                     >
                        {{ prompt }}
                     </button>
                  </div>
               </div>

               <!-- Conversation -->
               <div v-else class="flex flex-col gap-6">
                  <ChatMessage
                     v-for="message in sessionMessages"
                     :key="message.id"
                     :message="message"
                  />
               </div>
            </div>

            <!-- Input -->
            <div class="px-5 pb-5 pt-2 shrink-0">
               <p v-if="errorMessage" class="text-red-500 text-center text-xs mb-2">
                  {{ errorMessage }}
               </p>
               <ChatInput
                  v-model="chatInput"
                  bordered
                  :is-loading="isSending"
                  @send-message="handleSendMessage"
               />
            </div>
         </div>
      </div>
   </Transition>
</template>

<style scoped lang="postcss">
/* Water-bubble: a glossy translucent droplet. A radial fill gives the spherical
   body, the layered shadows read as refraction + volume, and ::before / ::after
   paint the specular highlight and a small sparkle so it looks wet. */
.luma-bubble {
   /* NOTE: no `position` here — scoped styles outrank Tailwind's `.fixed`
      utility, so setting position would knock the fixed trigger out of place.
      Elements that need a positioning context add `relative` in the template. */
   isolation: isolate;
   overflow: hidden;
   background: radial-gradient(
      125% 125% at 32% 24%,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(255, 255, 255, 0.4) 20%,
      rgba(150, 214, 210, 0.32) 48%,
      rgba(2, 77, 80, 0.34) 100%
   );
   backdrop-filter: blur(8px) saturate(180%);
   -webkit-backdrop-filter: blur(8px) saturate(180%);
   border: 1px solid rgba(255, 255, 255, 0.65);
   box-shadow:
      0 12px 28px rgba(2, 77, 80, 0.30),
      0 2px 8px rgba(2, 77, 80, 0.18),
      inset 0 2px 4px rgba(255, 255, 255, 0.9),
      inset 0 -10px 20px rgba(2, 77, 80, 0.22),
      inset 0 0 0 1px rgba(255, 255, 255, 0.25);
}

/* Big soft specular highlight (top-left) */
.luma-bubble::before {
   content: '';
   position: absolute;
   top: 8%;
   left: 14%;
   width: 46%;
   height: 34%;
   border-radius: 50%;
   background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 72%);
   filter: blur(0.5px);
   pointer-events: none;
   z-index: 5;
}

/* Gentle "swimming" idle motion for the floating seahorse — a slow bob + sway,
   as if drifting inside the bubble. */
@keyframes luma-swim {
   0%, 100% {
      transform: translateY(0) rotate(0deg);
   }
   25% {
      transform: translateY(-2.5px) rotate(-4deg);
   }
   50% {
      transform: translateY(0.5px) rotate(0deg);
   }
   75% {
      transform: translateY(-1.5px) rotate(4deg);
   }
}

.luma-swim {
   animation: luma-swim 5s ease-in-out infinite;
   transform-origin: 50% 70%;
   will-change: transform;
}

@media (prefers-reduced-motion: reduce) {
   .luma-swim {
      animation: none;
   }
}

/* Small sparkle (bottom-right) */
.luma-bubble::after {
   content: '';
   position: absolute;
   right: 20%;
   bottom: 22%;
   width: 12%;
   height: 12%;
   border-radius: 50%;
   background: rgba(255, 255, 255, 0.85);
   filter: blur(0.5px);
   pointer-events: none;
   z-index: 5;
}

.luma-glass-panel {
   background: linear-gradient(160deg, rgba(255, 255, 255, 0.9), rgba(240, 250, 249, 0.82));
   backdrop-filter: blur(28px) saturate(180%);
   -webkit-backdrop-filter: blur(28px) saturate(180%);
}

/* Trigger pop */
.luma-trigger-enter-active,
.luma-trigger-leave-active {
   transition: opacity 200ms ease, transform 200ms ease;
}
.luma-trigger-enter-from,
.luma-trigger-leave-to {
   opacity: 0;
   transform: scale(0.6);
}

/* Past-chats rail reveal — fade + collapse width so the conversation slides over as it opens */
.luma-rail-enter-active,
.luma-rail-leave-active {
   transition: opacity 240ms ease, width 300ms cubic-bezier(0.32, 0.72, 0, 1);
   overflow: hidden;
}
.luma-rail-enter-from,
.luma-rail-leave-to {
   opacity: 0;
   width: 0;
}

/* Panel slides in from the right */
.luma-panel-enter-active,
.luma-panel-leave-active {
   transition: transform 320ms cubic-bezier(0.32, 0.72, 0, 1), opacity 320ms ease;
}
.luma-panel-enter-from,
.luma-panel-leave-to {
   transform: translateX(100%);
   opacity: 0.4;
}
</style>
